import React, { useState, useEffect } from "react";
import { jsPDF } from "jspdf";
import { useNavigate } from "react-router-dom"; // ✅ import navigate

const BanquetSubscription = () => {
  const [showTerms, setShowTerms] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [successPopup, setSuccessPopup] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const navigate = useNavigate();

  // ✅ Banquet Hall subscription plans
  const plans = [
    {
      id: 1,
      title: "Basic Banquet Plan",
      price: 9999,
      features: [
        "Banquet hall booking portal access",
        "Event calendar management",
        "Basic vendor support",
        "Customer inquiry management",
        "Standard hall promotion listing",
        "Mobile-friendly dashboard",
        "Basic event reports",
        "Dedicated support team",
        "5 free event promotions",
        "Guidance for compliance & licenses",
      ],
    },
    {
      id: 2,
      title: "Standard Banquet Plan",
      price: 19999,
      features: [
        "All Basic Plan features",
        "Priority event listing",
        "Advanced vendor support",
        "Customer loyalty system setup",
        "Hall promotion through social media",
        "Detailed event analytics dashboard",
        "Unlimited event promotions",
        "Preferred vendor network access",
        "Seasonal marketing campaigns",
        "Dedicated banquet account manager",
      ],
    },
    {
      id: 3,
      title: "Premium Banquet Plan",
      price: 29999,
      features: [
        "All Standard Plan features",
        "Exclusive premium event promotion",
        "High-priority search listing",
        "Premium vendor & catering partnerships",
        "Event sponsorship support",
        "Custom banquet hall branding",
        "Dedicated marketing team",
        "Personalized event consultant",
        "VIP customer leads",
        "Lifetime support & updates",
      ],
    },
  ];

  // ✅ Razorpay SDK Loader
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
      } else {
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
      }
    });
  };

  // ✅ Generate PDF Invoice
  const generatePDF = (plan) => {
    const doc = new jsPDF();
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Invoice Summary", 105, 20, { align: "center" });

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");

    const data = {
      owner: "Banquet Owner",
      company: "Maamaas House",
      vertical: "Banquet Hall Business",
      plan: plan.title,
      amount: `₹${plan.price}`,
    };

    let y = 40;
    const lineGap = 10;

    Object.entries(data).forEach(([label, value]) => {
      doc.text(`${label.charAt(0).toUpperCase() + label.slice(1)}:`, 20, y);
      doc.text(value, 80, y);
      y += lineGap;
    });

    doc.setFont("helvetica", "bold");
    doc.text("TOTAL", 140, y + 10, { align: "right" });
    doc.text(`₹${plan.price}`, 190, y + 10, { align: "right" });

    doc.setFont("helvetica", "normal");
    doc.text("Terms & Conditions apply.", 20, y + 30);

    doc.save("banquet_invoice.pdf");
  };

  // ✅ Open Razorpay Payment
  const initiatePayment = async (plan) => {
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      alert("Failed to load Razorpay. Try again.");
      return;
    }

    const options = {
      key: "rzp_live_RU5whSMu9rPV7s",
      amount: plan.price * 100,
      currency: "INR",
      name: "Maamaas House",
      description: plan.title,
      handler: function (response) {
        generatePDF(plan);
        setShowTerms(false);
        setSuccessPopup(true);
      },
      prefill: {
        name: "Customer Name",
        email: "customer@example.com",
        contact: "9999999999",
      },
      theme: { color: "#3399cc" },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  // ✅ Auto close success popup
  useEffect(() => {
    if (successPopup) {
      setCountdown(5);

      const interval = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);

      const timer = setTimeout(() => {
        setSuccessPopup(false);
        clearInterval(interval);

        // ✅ Navigate after popup closes
        navigate("/");  // <-- change path as needed
      }, 5000); // ⏱ match countdown (5 sec)

      return () => {
        clearInterval(interval);
        clearTimeout(timer);
      };
    }
  }, [successPopup, navigate]);

  return (
    <div style={styles.wrapper}>
      <h1 style={styles.mainHeading}>Banquet Hall Subscription</h1>

      <div style={styles.container}>
        {plans.map((plan) => (
          <div key={plan.id} style={styles.card}>
            <h2 style={styles.cardTitle}>{plan.title}</h2>
            <p style={styles.price}>₹{plan.price} / month</p>
            <ul style={styles.list}>
              {plan.features.map((item, i) => (
                <li key={i} style={styles.listItem}>
                  ✅ {item}
                </li>
              ))}
            </ul>
            <button
              style={styles.button}
              onClick={() => {
                setSelectedPlan(plan);
                setShowTerms(true);
              }}
            >
              Pay Now
            </button>
          </div>
        ))}
      </div>

      {/* Terms Popup */}
      {showTerms && (
        <div style={styles.overlay}>
          <div style={styles.popup}>
            <h3 style={{ marginBottom: "15px" }}>Terms & Conditions</h3>
            <label
              style={{
                display: "block",
                margin: "15px 0",
                fontSize: "14px",
                color: "#333",
              }}
            >
              <input
                type="checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                style={{ marginRight: "8px" }}
              />
              I agree to the{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#007bff", textDecoration: "underline" }}
              >
                Terms & Conditions
              </a>{" "}
              and{" "}
              <a
                href="/privacy"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#007bff", textDecoration: "underline" }}
              >
                Privacy Policy
              </a>
            </label>

            <button
              style={styles.submitBtn}
              onClick={() => {
                if (!accepted) {
                  alert("Please accept Terms & Conditions first!");
                  return;
                }
                initiatePayment(selectedPlan);
              }}
            >
              Confirm & Pay
            </button>
            <button
              style={styles.closeBtn}
              onClick={() => setShowTerms(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Success Popup */}
      {successPopup && (
        <div style={styles.overlay}>
          <div style={styles.popup}>
            <h2>🎉 Payment Successful!</h2>
            <p>
              Your payment is successful. <br />
              Invoice has been generated. <br />
              Please check your email for confirmation.
            </p>
            <p
              style={{
                fontSize: "25px",
                marginTop: "10px",
                color: "#c83ecfff",
                fontWeight: "bold",
              }}
            >



              {countdown} seconds.
            </p>
          </div>
        </div>

      )}




    </div>
  );
};

// ✅ Inline Styles
const styles = {
  wrapper: {
    maxWidth: "1017px",
    margin: "0 auto",
    padding: "20px",
    textAlign: "center",
  },
  mainHeading: {
    fontSize: "32px",
    fontWeight: "bold",
    marginBottom: "30px",
    color: "#222",
  },
  container: {
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "20px",
  },
  card: {
    flex: "1 1 275px",
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    padding: "20px",
    textAlign: "center",
  },
  cardTitle: {
    fontSize: "22px",
    fontWeight: "bold",
    marginBottom: "10px",
    color: "#333",
  },
  price: {
    fontSize: "20px",
    fontWeight: "bold",
    color: "#ab56c1ff",
    marginBottom: "15px",
  },
  list: {
    listStyle: "none",
    padding: 0,
    textAlign: "left",
    marginBottom: "20px",
    fontSize: "14px",
    color: "#444",
  },
  listItem: { marginBottom: "8px" },
  button: {
    backgroundColor: "#ab4cc0ff",
    color: "#fff",
    padding: "10px 20px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "bold",
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  popup: {
    background: "#fff",
    padding: "30px",
    borderRadius: "12px",
    width: "90%",
    maxWidth: "400px",
    textAlign: "center",
    position: "relative",
  },
  submitBtn: {
    backgroundColor: "#ce6ce0ff",
    color: "#fff",
    padding: "12px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "bold",
    marginTop: "10px",
    marginRight: "10px",
  },
  closeBtn: {
    backgroundColor: "#bf2c2cff",
    color: "#fff",
    padding: "12px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "bold",
    marginTop: "10px",
  },
};

export default BanquetSubscription;
